// src/pages/providers/2_dashboard/components/ClinicianRemoveForm.js
import React, { useState, useContext } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { ExclamationIcon } from '@heroicons/react/solid';
import { auth, functions } from '../../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';

function ClinicianRemoveForm({ isOpen, onClose, clinician, onSuccess }) {
    const [deleteText, setDeleteText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    // Assigned patients are fetched as a read-only preview so the admin can see
    // who is affected BEFORE committing to a removal they cannot undo.
    const [assignedPatients, setAssignedPatients] = useState(null);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const { refreshProviderAccountsData } = useContext(GlobalStateContext);

    // Reset states when dialog opens
    React.useEffect(() => {
        if (isOpen) {
            setDeleteText('');
            setIsDeleting(false);
            setError(null);
            setSuccess(false);
            setAssignedPatients(null);
        }
    }, [isOpen]);

    // Fetch the assigned-patient list via the callable's dryRun mode, which is
    // a pure read. Mirrors the Disable Account flow so both destructive staff
    // actions warn about care-continuity the same way.
    //
    // A failure here is deliberately non-blocking: the preview is advisory, and
    // refusing to let an admin remove an account because a warning could not be
    // loaded would be worse than showing the warning-less dialog.
    React.useEffect(() => {
        if (!isOpen || !clinician?.clinicianId) return;

        let cancelled = false;
        setIsLoadingPatients(true);

        (async () => {
            try {
                const archiveClinicianFn = httpsCallable(functions, 'archiveClinician');
                const result = await archiveClinicianFn({
                    clinicianId: clinician.clinicianId,
                    dryRun: true,
                });
                if (cancelled) return;
                setAssignedPatients(result?.data?.patients || []);
            } catch (err) {
                if (cancelled) return;
                console.warn('Failed to load assigned patients for removal preview:', err);
                setAssignedPatients(null);
            } finally {
                if (!cancelled) setIsLoadingPatients(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isOpen, clinician?.clinicianId]);

    const handleDeleteClinician = async () => {
        if (deleteText !== 'DELETE' || !clinician) return;

        setIsDeleting(true);
        setError(null);

        try {
            console.log("Attempting to delete clinician with ID:", clinician.clinicianId);
            const archiveClinicianFn = httpsCallable(functions, 'archiveClinician');
            await archiveClinicianFn({
                clinicianId: clinician.clinicianId,
                reason: `${auth.currentUser?.displayName || 'Admin'} requested deletion via dashboard`
            });

            setSuccess(true);

            // Refresh the providers list
            await refreshProviderAccountsData();

            // Call the success callback if provided
            if (onSuccess) {
                onSuccess();
            }

            // Auto close after success
    setTimeout(() => {
        onClose();
    }, 1000);
} catch (err) {
    console.error('Error archiving clinician:', err);
    // More detailed error logging
    if (err.code) console.error(`Error code: ${err.code}`);
    if (err.details) console.error(`Error details: ${err.details}`);
    if (err.message) console.error(`Error message: ${err.message}`);
    
    setError(err.message || 'An error occurred while trying to delete the clinician.');
} finally {
            setIsDeleting(false);
        }
    };

    const isDeleteEnabled = deleteText === 'DELETE' && !isDeleting;

    return (
        <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
            <AlertDialog.Portal>
                <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50" />
                <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 max-w-md w-[90vw] max-h-[85vh] overflow-auto">
                    <AlertDialog.Title className="text-xl font-semibold text-gray-900 flex items-center">
                        <ExclamationIcon className="w-6 h-6 text-red-500 mr-2" />
                        Delete User Account
                    </AlertDialog.Title>

                    <div className="mt-3 text-sm text-gray-700">
                        <AlertDialog.Description asChild>
                            <p className="mb-2">
                                You are about to permanently remove:
                            </p>
                        </AlertDialog.Description>

                        <p className="font-medium text-gray-900 mb-4">
                            {clinician?.title} {clinician?.firstName} {clinician?.lastName} ({clinician?.email})
                        </p>

                        <div className="bg-red-50 rounded-lg p-4 mb-4">
                            <div className="flex items-start">
                                <div className="">
                                    <p className="text-sm text-red-700">
                                        This action cannot be undone. The user will be removed
                                        from the staff list and their account disabled, so they
                                        will no longer be able to sign in.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Care-continuity warning.
                            Removal does NOT reassign patients, so any patients this person is
                            assigned to keep them on their care team with no active account
                            behind it. The admin has to reassign them deliberately, which is why
                            we name the patients here rather than just giving a count. */}
                        {isLoadingPatients && (
                            <p className="text-sm text-gray-500 mb-4">
                                Checking for assigned patients...
                            </p>
                        )}

                        {!isLoadingPatients && assignedPatients && assignedPatients.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                                <p className="text-sm text-amber-800 font-medium mb-1">
                                    Still assigned to {assignedPatients.length}{' '}
                                    patient{assignedPatients.length === 1 ? '' : 's'}
                                </p>
                                <p className="text-sm text-amber-700 mb-3">
                                    Removing this account does not reassign their patients.
                                    Reassign these patients to another clinician to avoid a gap
                                    in their care.
                                </p>
                                <div
                                    data-testid="remove-assigned-patients-list"
                                    className="max-h-40 overflow-y-auto overscroll-contain border border-amber-200 rounded-md divide-y divide-amber-100 bg-white"
                                >
                                    {assignedPatients.map((patient) => (
                                        <div
                                            key={patient.patientId}
                                            className="px-3 py-2 text-xs text-gray-700 flex items-baseline justify-between gap-3"
                                        >
                                            <span className="min-w-0 break-words font-medium">
                                                {patient.patientName}
                                            </span>
                                            <span className="flex-shrink-0 text-gray-500 tabular-nums">
                                                {patient.referenceNumber || 'No reference'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-900 mb-4">
                                Type DELETE in all caps to confirm:
                            </label>
                            <input
                                type="text"
                                value={deleteText}
                                onChange={(e) => setDeleteText(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                placeholder="Type DELETE here"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="mt-4 bg-red-50 border-l-4 border-red-400 p-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">
                                        {error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {success && (
                        <div className="mt-4 bg-green-50 border-l-4 border-green-400 p-4">
                            <div className="flex">
                                <div className="ml-3">
                                    <p className="text-sm text-green-700">
                                        User account successfully deleted.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                        <AlertDialog.Cancel asChild>
                            <button
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                        </AlertDialog.Cancel>

                        <button
                            onClick={handleDeleteClinician}
                            disabled={!isDeleteEnabled}
                            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDeleteEnabled
                                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                    : 'bg-red-300 cursor-not-allowed'
                                }`}
                        >
                            {isDeleting ? (
                                <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Deleting...
                                </span>
                            ) : (
                                'Delete User'
                            )}
                        </button>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
}

export default ClinicianRemoveForm;